/**
 * Aware Partner Network — Phase 27 tracking helper.
 *
 * Fire-and-forget tracking for the booking funnel. Strictly:
 *   • Never blocks the booking flow — every send is best-effort.
 *   • Never throws.
 *   • Never sends PII (no name, email, mobile, address, payment).
 *   • Never reads cookies — uses only an anonymous session UUID stored
 *     in sessionStorage so it expires when the tab closes.
 *   • Uses navigator.sendBeacon where available so the event survives
 *     a page unload (useful for booking_checkout_started right before
 *     the Stripe redirect).
 */
import { API_BASE } from "@/lib/api";

const SESSION_KEY = "pws_tracking_session_id";

function _safe(value, fallback) {
  try { return value(); } catch { return fallback; }
}

function _genUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback — best-effort UUID v4 shape.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Returns the anonymous session id, minting one on first use. */
export function getSessionId() {
  return _safe(() => {
    if (typeof sessionStorage === "undefined") return _genUuid();
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = _genUuid();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  }, _genUuid());
}

function _uaFamily() {
  return _safe(() => {
    const ua = (navigator.userAgent || "").toLowerCase();
    if (ua.includes("edg/")) return "edge";
    if (ua.includes("chrome") && !ua.includes("edg/")) return "chrome";
    if (ua.includes("safari") && !ua.includes("chrome")) return "safari";
    if (ua.includes("firefox")) return "firefox";
    if (ua.includes("samsungbrowser")) return "samsung";
    if (ua.includes("opera")) return "opera";
    if (ua.includes("android")) return "android";
    if (ua.includes("iphone") || ua.includes("ipad")) return "ios";
    return "other";
  }, null);
}

function _utm() {
  return _safe(() => {
    const sp = new URLSearchParams(window.location.search);
    const out = {};
    ["utm_source", "utm_medium", "utm_campaign",
     "utm_term", "utm_content"].forEach((k) => {
      const v = sp.get(k);
      if (v) out[k] = v;
    });
    return Object.keys(out).length ? out : null;
  }, null);
}

/**
 * Fire-and-forget event sender.
 * `opts.useBeacon` switches to `navigator.sendBeacon` for unload-safe
 * delivery (used by `booking_checkout_started` right before the
 * Stripe redirect).
 */
export function trackEvent(eventType, fields = {}, opts = {}) {
  try {
    const body = JSON.stringify({
      event_type:         eventType,
      session_id:         getSessionId(),
      partner_slug:       fields.partnerSlug || null,
      booking_reference:  fields.bookingReference || null,
      attribution_source: fields.attributionSource || null,
      current_step:       fields.currentStep || null,
      test_id:            fields.testId || null,
      utm:                _utm(),
      user_agent_family:  _uaFamily(),
    });
    const url = `${API_BASE}/pws/tracking/event`;
    if (opts.useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    // best-effort fetch — keep it silent on failure.
    if (typeof fetch === "function") {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // never throw from a tracker
  }
}

export default trackEvent;
